/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Col, Row } from 'react-bootstrap';
import { get, isEqual, max } from 'lodash';
import numeral from 'numeral';

import ComponentHeader from '../../components/Headers/component-header';
import { truncateText } from '../../utils/shared';
import moviesConfig from '../configs/movies-config';
import { extractFilterValues } from '../../utils/filters';
import { FilterTypes } from '../../components/Filters';
import { TimeLine } from '../../components/Charts/TimeLine';
import {
  CountryDemo,
  AgeDemo,
  GenderDemo
} from '../../components/Charts/DemoCharts';
import { downloadReportFunc } from '../../utils/export';
import ModuleExport from '../../components/ModuleExport';

import {
  movieAnticipationFollowersTotal,
  movieAnticipationFollowersNew,
  formatGenderDemo
} from './table-config';
import './styles/MovieInsights.css';

const MovieAnticipation = ({
  queries: {
    movieAnticipationFollowers,
    movieFollowerCountryIndex,
    movieFollowerAgeIndex,
    movieFollowerGenderIndex
  },
  queryActions: { runFormattedInlineQueriesForPage },
  filters,
  updateBenchmarkData,
  blocked,
  movie
}) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const filterFields = filters && filters.fields;

  useEffect(() => {
    const updateQueries = () => {
      runFormattedInlineQueriesForPage(
        moviesConfig.MOVIE_REPORT_FILTERS[
          moviesConfig.MOVIE_ANTICIPATION_PAGE_ID
          ],
        filterFields
      );
      updateBenchmarkData(filterFields, null, true);
    };

    if (blocked) {
      return;
    }
    const values = extractFilterValues(filterFields);
    const hasNewValues = !isEqual(values, selectedFilters);

    if (hasNewValues) {
      setSelectedFilters(values);
      updateQueries();
    }
  }, [blocked, filterFields, runFormattedInlineQueriesForPage, updateBenchmarkData]);

  return (
    <Grid fluid componentClass="section">
      <Row>
        <Col xs={12} className="show-report-line-col">
          <ComponentHeader title="New Followers" />
          <ModuleExport
            fetchData
            showTitle={get(movie, 'name', '')}
            tableDataDownload={downloadReportFunc(
              'movieAnticipationFollowers',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportAnticipation.queries
                .movieAnticipationFollowers,
              ''
            )}
            csv
            xls
            png
          />
          <div className="tvt-show-report__anticipation-line-chart-wrapper">
            <TimeLine
              id={get(movie, 'name', 'this movie')}
              data={movieAnticipationFollowersNew(
                get(movieAnticipationFollowers, 'data', [])
              )}
              startDate="2019-07-12"
              loading={get(movieAnticipationFollowers, 'loading', true)}
              dateFilter={get(
                filters,
                ['fields', 'date', 'value', 'value'],
                null
              )}
              color="hsl(182, 38%, 62%)"
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="show-report-line-col">
          <ComponentHeader title="Total Followers" />
          <ModuleExport
            fetchData
            showTitle={get(movie, 'name', '')}
            tableDataDownload={downloadReportFunc(
              'movieAnticipationFollowers',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportAnticipation.queries
                .movieAnticipationFollowers,
              ''
            )}
            csv
            xls
            png
          />
          <div className="tvt-show-report__anticipation-line-chart-wrapper">
            <TimeLine
              id={get(movie, 'name', 'this movie')}
              data={movieAnticipationFollowersTotal(
                get(movieAnticipationFollowers, 'data', [])
              )}
              loading={get(movieAnticipationFollowers, 'loading', true)}
              startDate="2019-07-12"
              dateFilter={get(
                filters,
                ['fields', 'date', 'value', 'value'],
                null
              )}
              color="hsl(307, 45%, 63%)"
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={6} className="show-report-demo-col">
          <ComponentHeader
            title={
              <span>
                Country Index of{' '}
                <em>{truncateText(get(movie, 'name', ''), 80)}</em> Followers
              </span>
            }
          />
          <ModuleExport
            fetchData
            showTitle={get(movie, 'name', '')}
            tableDataDownload={downloadReportFunc(
              'movieFollowerCountryIndex',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportAnticipation.queries
                .movieFollowerCountryIndex,
              'index'
            )}
            csv
            xls
            png
          />
          <div className="tvt-show-report__anticipation-countryDemo-chart-wrapper">
            <CountryDemo
              maxValue={max(
                movieFollowerCountryIndex.data.map(c => Math.abs(c.index))
              )}
              query={movieFollowerCountryIndex}
              keys={['index']}
              indexKey="country"
              valueKey="index"
            />
          </div>
        </Col>
        <Col xs={6} className="show-report-demo-col">
          <ComponentHeader
            title={
              <span>
                Age of <em>{truncateText(get(movie, 'name', ''))}</em> Followers
              </span>
            }
            tooltip="This metric represents a normalized view of
              this defined audience. It is calculated
              relative to the overall app population to
              adjust for app population biases. The
              percentages represented do not reflect raw
              viewer demographics."
          />
          <ModuleExport
            fetchData
            showTitle={get(movie, 'name', '')}
            tableDataDownload={downloadReportFunc(
              'movieFollowerAgeIndex',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportAnticipation.queries
                .movieFollowerAgeIndex,
              'age'
            )}
            csv
            xls
            png
          />
          <div className="tvt-show-report__anticipation-ageDemo-chart-wrapper">
            <AgeDemo
              query={movieFollowerAgeIndex}
              layout="vertical"
              sortKey="age"
              indexKey="age"
              width={570}
              keys={['index']}
              tooltipFormatter={({ value }) =>
                `${numeral(value * 100).format('0.0')}%`
              }
            />
          </div>
          <div className="show_report__demo-chart-wrapper">
            <ComponentHeader
              title={
                <span>
                  Gender of <em>{truncateText(get(movie, 'name', ''))}</em>{' '}
                  Followers
                </span>
              }
              tooltip="This metric represents a normalized view of
              this defined audience. It is calculated
              relative to the overall app population to
              adjust for app population biases. The
              percentages represented do not reflect raw
              viewer demographics."
            />
            <ModuleExport
              fetchData
              showTitle={get(movie, 'name', '')}
              tableDataDownload={downloadReportFunc(
                'movieFollowerGenderIndex',
                filters.fields,
                moviesConfig.MOVIE_REPORT_FILTERS.movieReportAnticipation
                  .queries.movieFollowerGenderIndex,
                ''
              )}
              csv
              xls
              png
            />
            <div className="tvt-show-report__anticipation-genderDemo-chart-wrapper">
              <GenderDemo
                id="show_view_gender_index.viewer_gender"
                label="gender"
                value="index"
                layout="vertical"
                width={570}
                height={293}
                query={{
                  loading: movieFollowerGenderIndex.loading,
                  data: formatGenderDemo(movieFollowerGenderIndex)
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

MovieAnticipation.propTypes = {
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,
  blocked: PropTypes.bool.isRequired,
  updateBenchmarkData: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType, // eslint-disable-line react/no-typos
    options: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  queries: PropTypes.shape({
    movieAnticipationFollowers: PropTypes.shape({
      data: PropTypes.shape({}),
      loading: PropTypes.bool
    }).isRequired,
    movieFollowerAgeIndex: PropTypes.shape({
      data: PropTypes.shape({}),
      loading: PropTypes.bool
    }).isRequired,
    movieFollowerCountryIndex: PropTypes.shape({
      data: PropTypes.shape({}),
      loading: PropTypes.bool
    }).isRequired,
    movieFollowerGenderIndex: PropTypes.shape({
      data: PropTypes.shape({}),
      loading: PropTypes.bool
    }).isRequired
  }).isRequired,
  movie: PropTypes.shape({
    name: PropTypes.string.isRequired
  }).isRequired
};

MovieAnticipation.defaultProps = {};

export default MovieAnticipation;
